/**
 * What the interact prompt says — the one rule, written once.
 *
 * `docs/stories/TN-REACH-what-is-in-reach.md` is the acceptance criteria, and
 * the defect it exists to make impossible is worth restating here because it is
 * the reason this file is a module rather than three lines at a call site:
 *
 * > A player riding through Toronto comes within reach of the level's one point
 * > of interest, and the HUD's button reads **"CN Tower"**.
 *
 * Two things were wrong with that. It said **what is there** rather than what
 * choosing it will do, so a screen-reader user was read a noun with no verb. And
 * it put a trade name inside `hud`, which `TN-NAMES-04` fails the build for —
 * except that the name was never a copy string. It was the level document's own
 * `pois[].name`, interpolated at runtime by the composition root, which is
 * precisely how it walked past a check written against copy tables.
 *
 * So the prompt is resolved **only** from the copy table, and this function is
 * the only way to resolve one. A target this build has no row for offers no
 * prompt at all: `null`, never "Interact", never "Engage", never a name, never
 * an empty string. That is the same rule `TN-WAIT` applies to a level with no
 * waiting sentence, for the same reason — a screen that has to invent a word
 * ends up inventing the wrong one.
 *
 * ## The order, and why "done" wins
 *
 *  1. **The target has been engaged already** → `hud.interact.done`, whatever
 *     kind it is and whatever row its level wrote. The state is the news; the
 *     invitation is not, and a player told "Talk to the officer" about somebody
 *     they have finished with walks back for nothing.
 *  2. **The level wrote a row for this target** → `hud.interact.<id>`, keyed on
 *     the id the level document gives it. Ottawa writes two.
 *  3. **Otherwise, by kind** → `hud.interact.npc` for a person;
 *     `hud.interact.poi.offer` for a place that offers this level's quest;
 *     `hud.interact.poi` for any other place.
 *
 * ## Why a place that offers a quest has a row of its own
 *
 * ADR-0029 let a point of interest be a quest giver, so choosing Peggy's Point
 * Lighthouse opens a **dialogue with a task in it** rather than a card. "Look at
 * this place" is then the wrong promise, and a prompt that under-promises what
 * pressing does is this file's defect arriving from the other side.
 *
 * Two things the flag deliberately does **not** do, both of them defects that
 * were live in the composition root the day before this row existed:
 *
 *  - **It does not change the target's kind.** A place that offers something is
 *    still a place. `promptTargets` used to answer `canEngage(poi.id) ? 'npc' :
 *    'poi'`, so a lighthouse offered "Talk to this person" on a level whose art
 *    document forbids a figure at any scale precisely so nobody is read as being
 *    there — and it also broke the `poi.<id>` spelling the scene sends.
 *  - **It says nothing about a person.** A character who offers a quest draws no
 *    new row: "Talk to the officer" already says what pressing does, because
 *    talking is what a person does whether or not they have a task
 *    (`TN-REACH-02`).
 *
 * DOM only, and in fact no DOM at all: every function here is pure, so the rule
 * is assertable without a browser and the HUD cannot hold a second copy of it.
 */

import { hasCopyRow, text, type UiLocale } from './copy';

/** A person or a place. The two kinds a level's document can put in reach. */
export type InteractKind = 'poi' | 'npc';

/**
 * One thing in reach.
 *
 * `id` is the level document's own id — `officer`, `parliament-hill`,
 * `cn-tower` — because that is what the scene sends with `poi/entered` and what
 * a per-target row is keyed on. See {@link bareTargetId} for the prefixed spelling
 * the same id also arrives in.
 */
export interface ReachableTarget {
  readonly id: string;
  readonly kind: InteractKind;
  /** Engaged already in this sitting. `TN-REACH-03`: this beats everything. */
  readonly done?: boolean;
  /**
   * Choosing this target opens a dialogue with a task in it: it is this level's
   * quest giver (ADR-0029).
   *
   * Read **only for a place**, and only when no row was written for the target
   * itself. It picks a different generic row; it never makes a place a person.
   * Absent means "no", which is what every landmark that opens a card is.
   */
  readonly offersQuest?: boolean;
}

/**
 * The id, without the `poi.`/`npc.` prefix an event detail may carry.
 *
 * The stories write a detail two ways — `TN-QUEST-01` emits `poi/entered` for
 * `npc.officer`, and the level document calls the same character `officer` —
 * and a copy row is keyed on the document's id. Rather than pick a winner and
 * make the other spelling silently offer nothing, the prefix is stripped here
 * and the composition root registers both spellings. A detail with no prefix is
 * returned unchanged, which is the normal case.
 *
 * The *kind* is deliberately not read back out of a prefix. What a target is —
 * a person or a place — is the level document's answer, and taking it from an
 * event name instead would let a scene decide that a landmark is a character.
 */
export function bareTargetId(detail: string): string {
  const dot = detail.indexOf('.');
  if (dot === -1) return detail;
  const head = detail.slice(0, dot);
  return head === 'poi' || head === 'npc' ? detail.slice(dot + 1) : detail;
}

/**
 * What the prompt for this target says, or `null` when this build has no row.
 *
 * `null` is a state and not a failure: `TN-REACH-05` requires a target with no
 * row to offer **no prompt**, so the HUD draws nothing rather than something it
 * had to make up.
 */
export function interactPrompt(locale: UiLocale, target: ReachableTarget): string | null {
  if (target.done === true) return text(locale, 'hud.interact.done');

  const own = `hud.interact.${bareTargetId(target.id)}`;
  if (hasCopyRow(own)) return text(locale, own);

  /*
   * The kind, and the one case where a kind has two rows.
   *
   * `poi.offer` is asked for **only** when this build wrote it: `TN-REACH-05`
   * requires a missing generic row to fail the build and says the prompt is
   * "never drawn as 'Look at this place' in its place", so there is no fallback
   * from the offer row to the plain one. A place whose offer row is missing
   * offers no prompt at all, exactly as any other missing row does — a HUD that
   * quietly substitutes a weaker promise is how nobody finds out the row is
   * gone.
   */
  const offering = target.kind === 'poi' && target.offersQuest === true;
  const generic = offering ? 'hud.interact.poi.offer' : `hud.interact.${target.kind}`;
  return hasCopyRow(generic) ? text(locale, generic) : null;
}

/**
 * The one-time explanation of the marks in the world.
 *
 * It names no input — not "tap", not a key, not "hold" — because this game is
 * playable with a thumb, a keyboard and one switch, and a hint that names one of
 * them is wrong for the other three. "Choose it" is the word the single-switch
 * contract already uses.
 */
export function interactHint(locale: UiLocale): string {
  return text(locale, 'hud.interact.hint');
}
